import { ExternalLink, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useRecordAutodeskPush } from "@/hooks/use-autodesk";
import { useSendRecordToAutodesk } from "@/hooks/use-autodesk-mutations";
import { ApiError } from "@/lib/api";
import { useOnlineStatus } from "@/offline/online-status";
import { autodeskPushIndicator } from "./autodesk-push-indicator";

interface Props {
  projectId: string;
  recordId: string;
  hasActiveConnection: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

/** Pull the backend's `{detail}` off an ApiError body when present — its
 * `.message` is just "API error {status}", not the actual server text.
 * Mirrors `autodesk-section.tsx`'s `mutationMessage` helper. */
function mutationMessage(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    const body = error.body as { detail?: string } | null | undefined;
    // Only the backend's own words. An ApiError always carries a `.message`,
    // but it reads "API error 500" — which used to sit in a small inline line
    // and now goes in a toast, where developer-speak has no business. Falling
    // through to undefined lets the caller's generic copy win, which is what
    // this helper was always documented to do.
    return body?.detail;
  }
  return (error as Error | undefined)?.message;
}

/**
 * The Autodesk push affordance, as one row inside the record's Share menu.
 *
 * It used to be a full-width button in the view-actions bar; "Send to Autodesk"
 * is wide enough that it pushed the bar past a phone's width. It now lives
 * under Share — see [[record-share-menu.spec]] — which is also why send errors
 * toast rather than rendering inline: the menu closes on select, so an inline
 * line beneath the row would vanish with it.
 */
export function AutodeskPushMenuItem({
  projectId,
  recordId,
  hasActiveConnection,
  disabled = false,
  disabledReason,
}: Props) {
  const isOnline = useOnlineStatus();
  const { data: push, error } = useRecordAutodeskPush(
    projectId,
    recordId,
    hasActiveConnection,
  );
  const send = useSendRecordToAutodesk(projectId, recordId);

  if (!hasActiveConnection) return null;

  const indicator = autodeskPushIndicator(push, error);
  if (indicator === "hidden") return null;

  const blocked = disabled || !isOnline;
  const blockedReason = !isOnline
    ? "Offline — reconnect to send"
    : disabledReason;

  const fire = () =>
    send.mutate(undefined, {
      onError: (err) =>
        toast.error(mutationMessage(err) ?? "Couldn't send. Try again."),
    });

  if (indicator === "loading") {
    return (
      <DropdownMenuItem disabled aria-label="Loading Autodesk status">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking Autodesk…
      </DropdownMenuItem>
    );
  }

  if (indicator === "sending") {
    return (
      <DropdownMenuItem disabled>
        <Loader2 className="h-4 w-4 animate-spin" />
        Sending to Autodesk…
      </DropdownMenuItem>
    );
  }

  if (indicator === "linked") {
    return (
      <DropdownMenuItem asChild>
        <a
          href={push?.issue_url ?? "#"}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLink className="h-4 w-4" />
          In Autodesk
          {push?.forma_display_id != null ? ` #${push.forma_display_id}` : ""}
        </a>
      </DropdownMenuItem>
    );
  }

  if (indicator === "failed") {
    return (
      <DropdownMenuItem
        onSelect={fire}
        disabled={blocked || send.isPending}
        title={blockedReason}
        className="flex-col items-start gap-0.5"
      >
        <span className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Retry send to Autodesk
        </span>
        {/* The reason the last push failed — the one piece of the old inline
            error line worth keeping on screen rather than in a toast. */}
        <span className="pl-6 text-xs text-destructive">
          {push?.error_detail?.message ?? "Push failed"}
        </span>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      onSelect={fire}
      disabled={blocked || send.isPending}
      title={blockedReason}
      aria-label="Send to Autodesk"
    >
      <Upload className="h-4 w-4" />
      Send to Autodesk
    </DropdownMenuItem>
  );
}
